import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../../hooks";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { ControlEquipmentForm as ControlEquipmentFormType, getControlEquipmentFormSchema } from "../../../types/forms/capture";
import { ControlEquipment } from "../../../types/model/controlEquipment";
import FormAutocomplete from "../../General/FormAutocomplete";

interface ControlEquipmentFormProps {
  controlEquipmentData?: ControlEquipment;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: ControlEquipmentFormType) => Promise<void>;
}

export default function ControlEquipmentForm({
  controlEquipmentData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<ControlEquipmentFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getControlEquipmentFormSchema(t);
  const methods = useForm<ControlEquipmentFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      equipmentType: controlEquipmentData?.equipmentType ?? null,
      equipmentBrandId: controlEquipmentData?.equipmentBrandId ?? null,
      equipmentModel: controlEquipmentData?.equipmentModel ?? null,
      specs: controlEquipmentData?.specs ?? null,
      installationDate: controlEquipmentData?.installationDate ?? null,
      removalDate: controlEquipmentData?.removalDate ?? null,
    },
  });

  const { data: brands = [] } = useReferenceData("construction", "brands");
  
  const handleFormSubmit = async (values: ControlEquipmentFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.control-equipment.equipment-type")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("equipmentType")}
              error={!!methods.formState.errors.equipmentType}
              helperText={methods.formState.errors.equipmentType?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormAutocomplete
              name="equipmentBrandId" 
              label={t("captures.capture.control-equipment.equipment-brand")} 
              options={brands}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.control-equipment.equipment-model")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("equipmentModel")}
              error={!!methods.formState.errors.equipmentModel}
              helperText={methods.formState.errors.equipmentModel?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              fullWidth
              label={t("captures.capture.control-equipment.specs")}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("specs")}
              error={!!methods.formState.errors.specs}
              helperText={methods.formState.errors.specs?.message}
            />
          </Grid2>
          <Grid2 size={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="installationDate"
                control={methods.control}
                render={({ field }) => (
                  <DatePicker
                    label={t("captures.capture.control-equipment.installation-date")}
                    value={field.value ? dayjs(field.value, "YYYY-MM-DD") : null}
                    onChange={(date) => {
                      field.onChange(date ? date.format("YYYY-MM-DD") : undefined);
                    }}
                    format="DD/MM/YYYY"
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: isEditing ? "outlined" : "standard",
                        placeholder: "DD/MM/YYYY",
                        error: !!methods.formState.errors.installationDate,
                        helperText: methods.formState.errors.installationDate?.message,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="removalDate"
                control={methods.control}
                render={({ field }) => (
                  <DatePicker
                    label={t("captures.capture.control-equipment.removal-date")}
                    value={field.value ? dayjs(field.value, "YYYY-MM-DD") : null}
                    onChange={(date) => {
                      field.onChange(date ? date.format("YYYY-MM-DD") : undefined);
                    }}
                    format="DD/MM/YYYY"
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: isEditing ? "outlined" : "standard",
                        placeholder: "DD/MM/YYYY",
                        error: !!methods.formState.errors.removalDate,
                        helperText: methods.formState.errors.removalDate?.message,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}